import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail, Loader2 } from 'lucide-react';
import axios from 'axios';
import Seo from '../components/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const PURPOSE_OPTIONS = [
  'Report a bug',
  'Report a photo or wrong info',
  'How can I support Explora?',
  'Account issue',
  'Sponsor Program',
  'Feature Request',
  'Others',
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    purpose: '',
    subject: '',
    captcha: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.purpose) {
      setError('Please select a purpose for contacting us.');
      return;
    }

    if (parseInt(formData.captcha, 10) !== 21) {
      setError('Incorrect captcha answer. 15 + 6 = ?');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_BASE}/contact`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        purpose: formData.purpose,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        captcha: parseInt(formData.captcha, 10),
      });
      setSuccess(true);
      setFormData({ name: '', email: '', purpose: '', subject: '', captcha: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-gray-100 min-h-screen py-10 md:py-16 content-page" data-testid="contact-page">
      <Seo
        title="Contact Us"
        description="Get in touch with the Explora Mauritius team — report an issue, request a photo licence, or ask about supporting local photographers."
        canonical="/contact"
      />
      <div className="max-w-full md:max-w-[70%] mx-auto px-4 md:px-0">
        {/* Page Heading */}
        <header className="mb-10 md:mb-14">
          <h1 data-testid="contact-h1">Contact Us</h1>
          <h2 className="mt-3 text-gray-600" data-testid="contact-h2">
            Questions, support and project enquiries
          </h2>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          {/* Left: Form */}
          <section className="lg:col-span-2" data-testid="contact-form-section">
            <h3 className="mb-3" data-testid="contact-form-h3">
              Send Us a Message
            </h3>
            <p className="text-gray-600 leading-relaxed mb-8 text-justify">
              Contact Explora if you have a question about the platform, want to report a bug,
              found wrong information on a photo, need help with your account, want to support
              the project, or would like to discuss sponsorship or future improvements.
            </p>

            {success ? (
              <div
                className="bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm"
                data-testid="contact-success"
              >
                <div className="mx-auto w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-lg text-gray-900 font-medium">
                  Thanks for your message. We&rsquo;ll get back to you within 3 working days.
                </p>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  data-testid="contact-send-another-btn"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Name"
                    required
                    maxLength={200}
                    className="w-full bg-white rounded-full px-6 py-4 text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                    data-testid="contact-name-input"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                    required
                    maxLength={200}
                    className="w-full bg-white rounded-full px-6 py-4 text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                    data-testid="contact-email-input"
                  />
                </div>

                <select
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  required
                  className={`w-full bg-white rounded-full px-6 py-4 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition appearance-none ${formData.purpose ? 'text-gray-900' : 'text-gray-400'}`}
                  data-testid="contact-purpose-select"
                  aria-label="Purpose for contacting us"
                >
                  <option value="" disabled>Select a purpose...</option>
                  {PURPOSE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt} className="text-gray-900">{opt}</option>
                  ))}
                </select>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-5">
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Subject"
                    required
                    maxLength={300}
                    className="w-full bg-white rounded-full px-6 py-4 text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                    data-testid="contact-subject-input"
                  />
                  <input
                    type="number"
                    name="captcha"
                    value={formData.captcha}
                    onChange={handleChange}
                    placeholder="15 + 6 = ?"
                    required
                    inputMode="numeric"
                    className="w-full bg-white rounded-full px-6 py-4 text-gray-900 placeholder-gray-500 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition text-center"
                    data-testid="contact-captcha-input"
                    aria-label="Captcha: What is 15 plus 6?"
                  />
                </div>

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message"
                  required
                  rows={8}
                  maxLength={5000}
                  className="w-full bg-white rounded-3xl px-6 py-4 text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition resize-y min-h-[200px]"
                  data-testid="contact-message-input"
                />

                {error && (
                  <div
                    className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-red-800 text-sm"
                    data-testid="contact-error"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white font-semibold text-base md:text-lg rounded-full py-5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}
          </section>

          {/* Right: Contact Details + Links */}
          <aside className="lg:pl-6" data-testid="contact-sidebar">
            <h3 className="mb-6" data-testid="contact-details-h3">
              Contact Details
            </h3>
            <ul className="space-y-5 text-gray-700">
              <li className="flex items-center gap-4" data-testid="contact-phone">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 text-gray-900 shrink-0">
                  <Phone className="w-4 h-4" />
                </span>
                <a
                  href="tel:+23059402337"
                  className="text-base leading-none hover:text-gray-900 transition-colors"
                >
                  +230 5940 2337
                </a>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-address">
                <span className="mt-1 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 text-gray-900 shrink-0">
                  <MapPin className="w-4 h-4" />
                </span>
                <div className="text-base leading-snug">
                  <div>Albion, Mauritius</div>
                  <div className="text-gray-500">hi(at)explora.mu</div>
                </div>
              </li>
            </ul>

            <div className="my-10 border-t border-gray-200" />

            <h3 className="mb-6" data-testid="help-resources-h3">
              Help &amp; Resources
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/faqs"
                  className="text-base text-gray-700 hover:text-gray-900 hover:underline transition-colors"
                  data-testid="contact-link-faqs"
                >
                  FAQs
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-base text-gray-700 hover:text-gray-900 hover:underline transition-colors"
                  data-testid="contact-link-terms"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-base text-gray-700 hover:text-gray-900 hover:underline transition-colors"
                  data-testid="contact-link-privacy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="text-base text-gray-700 hover:text-gray-900 hover:underline transition-colors"
                  data-testid="contact-link-cookies"
                >
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
